import { useCallback, useEffect, useRef, useState, type UIEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { type NotificationItem } from '@todo/shared';
import { notificationApi } from '../api/notification.api';
import { useTaskViewer } from '../hooks/useTaskViewer';
import { NotificationCard } from './NotificationCard';

/**
 * The bell. NO timer anywhere: the unread count is fetched only on real
 * events - mount, the tab regaining focus, opening the panel, marking read.
 * An idle tab therefore costs the API nothing at all. The price is that a
 * new notification shows up on the next interaction rather than by itself;
 * true instant delivery is FCM push's job, not a polling loop's.
 */

const PAGE_SIZE = 15;

/** "3m ago" style, without a dependency. */
function timeAgo(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86_400)}d ago`;
}

/**
 * Where a notification leads when it points at no task and no event.
 *
 * `notifications` has task_id and event_id columns and nothing else, so a
 * CG Sign or Folder row carries neither - without this the click only
 * marked it read and went nowhere, which reads as a broken row.
 */
function destinationFor(kind: NotificationItem['kind']): string | null {
  if (kind === 'FOLDER_SHARED') return '/vault';
  if (kind.startsWith('SIGN_')) return '/cg-sign';
  return null;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { openTask, openTaskId } = useTaskViewer();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  /** Rows the server says exist, so we know when to stop asking. */
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  /** Guards against a second fetch firing from the same scroll gesture -
   *  state updates are async, so `loadingMore` alone is one render behind. */
  const fetching = useRef(false);

  const refreshCount = useCallback(() => {
    notificationApi
      .unreadCount()
      .then(setCount)
      .catch(() => undefined); // a failed refresh is not worth an error state
  }, []);

  /**
   * Event-driven, not timed: once on mount, then again whenever the person
   * comes BACK to the tab. Those are exactly the moments a stale badge would
   * be noticed - and the only requests the API pays for.
   */
  useEffect(() => {
    refreshCount();

    function onVisible() {
      if (document.visibilityState === 'visible') refreshCount();
    }

    window.addEventListener('focus', refreshCount);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refreshCount);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshCount]);

  // Close on any click outside the panel.
  useEffect(() => {
    if (!open) return;
    function onClick(event: MouseEvent) {
      if (!panelRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (!next) return;

    setLoading(true);
    refreshCount();
    try {
      const response = await notificationApi.list({ page: 1, pageSize: PAGE_SIZE });
      setItems(response.data);
      setTotal(response.meta?.total ?? response.data.length);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  /**
   * The next page, appended. Paged by PAGE NUMBER off the current length,
   * not a cursor: a notification arriving mid-scroll would shift an offset
   * and duplicate a row, so anything already held is filtered out on merge.
   */
  const loadMore = useCallback(async () => {
    if (fetching.current || items.length >= total) return;
    fetching.current = true;
    setLoadingMore(true);
    try {
      const response = await notificationApi.list({
        page: Math.floor(items.length / PAGE_SIZE) + 1,
        pageSize: PAGE_SIZE,
      });
      setItems((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...response.data.filter((n) => !seen.has(n.id))];
      });
      setTotal(response.meta?.total ?? total);
    } catch {
      /* Leave what is already on screen; the next scroll retries. */
    } finally {
      setLoadingMore(false);
      fetching.current = false;
    }
  }, [items.length, total]);

  /** Within 120px of the bottom is close enough to start fetching. */
  function onScroll(event: UIEvent<HTMLDivElement>) {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) void loadMore();
  }

  async function openOne(item: NotificationItem) {
    // Jump to the task (or event) first - reading it should never feel
    // gated on the mark-read call finishing.
    if (item.taskId) {
      openTask(item.taskId);
      setOpen(false);
    } else if (item.eventId) {
      navigate(`/calendar?event=${item.eventId}`);
      setOpen(false);
    } else {
      const to = destinationFor(item.kind);
      if (to) {
        navigate(to);
        setOpen(false);
      }
    }
    if (item.readAt) return;
    // Optimistic: flip locally, reconcile with the server in the background.
    setItems((prev) =>
      prev.map((n) =>
        n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n,
      ),
    );
    setCount((c) => Math.max(0, c - 1));
    await notificationApi.markRead([item.id]).catch(() => refreshCount());
  }

  async function readAll() {
    setItems((prev) =>
      prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })),
    );
    setCount(0);
    await notificationApi.markAllRead().catch(() => refreshCount());
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => void toggle()}
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        className="relative grid h-9 w-9 place-items-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
      >
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-5 w-5">
          <path
            d="M8 16a2 2 0 004 0M5 8a5 5 0 0110 0c0 3.5 1.2 4.5 1.2 4.5H3.8S5 11.5 5 8z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid min-w-[18px] place-items-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-[18px] text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-96 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <p className="text-sm font-semibold text-slate-900">Notifications</p>
            {count > 0 && (
              <button
                type="button"
                onClick={() => void readAll()}
                className="text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                Mark all read
              </button>
            )}
          </div>

          <div
            className="max-h-96 space-y-1.5 overflow-y-auto p-1.5"
            onScroll={onScroll}
          >
            {loading ? (
              <p className="px-4 py-8 text-center text-sm text-slate-400">Loading...</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-400">
                Nothing yet. Task assignments, completions and reminders land here.
              </p>
            ) : (
              items.map((item) => (
                <NotificationCard
                  key={item.id}
                  item={item}
                  active={item.taskId !== null && item.taskId === openTaskId}
                  timeLabel={timeAgo(item.createdAt)}
                  onOpen={() => void openOne(item)}
                />
              ))
            )}

            {!loading && items.length > 0 && (
              loadingMore ? (
                <p className="py-3 text-center text-xs text-slate-400">Loading more...</p>
              ) : items.length < total ? (
                /* The scroll handler normally gets there first; this is for
                   a trackpad that stops short, and for keyboard users. */
                <button
                  type="button"
                  onClick={() => void loadMore()}
                  className="w-full py-2.5 text-center text-xs font-medium text-brand-600 hover:text-brand-700"
                >
                  Show older ({total - items.length} more)
                </button>
              ) : (
                <p className="py-3 text-center text-xs text-slate-400">
                  That's everything.
                </p>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
